import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';

export class EffectsManager {
  private scene!: THREE.Scene;
  private renderer!: THREE.WebGLRenderer;
  private composer!: EffectComposer;
  private particles: THREE.Points[] = [];
  private cameraShake = { intensity: 0, decay: 0.9 };
  
  constructor(scene: THREE.Scene, renderer: THREE.WebGLRenderer) {
    this.scene = scene;
    this.renderer = renderer;
  }
  
  setupPostProcessing(camera: THREE.PerspectiveCamera): void {
    this.composer = new EffectComposer(this.renderer);
    
    const renderPass = new RenderPass(this.scene, camera);
    this.composer.addPass(renderPass);
    
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      0.8,
      0.4,
      0.85
    );
    this.composer.addPass(bloomPass);
    
    const glitchShader = {
      uniforms: {
        tDiffuse: { value: null },
        time: { value: 0 },
        amount: { value: 0 }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform float time;
        uniform float amount;
        varying vec2 vUv;
        
        float random(vec2 st) {
          return fract(sin(dot(st.xy, vec2(12.9898, 78.233))) * 43758.5453123;
        }
        
        void main() {
          vec2 uv = vUv;
          
          if (amount > 0.0) {
            float noise = random(uv + time);
            if (noise < amount * 0.1) {
              uv.x = 1.0 - uv.x;
            }
            if (noise < amount * 0.05) {
              uv.y = 1.0 - uv.y;
            }
          }
          
          vec4 color = texture2D(tDiffuse, uv);
          gl_FragColor = color;
        }
      `
    };
    
    const glitchPass = new ShaderPass(glitchShader);
    this.composer.addPass(glitchPass);
    
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;
  }
  
  setupParticles(): void {
    for (let i = 0; i < 50; i++) {
      const geo = new THREE.BufferGeometry();
      const positions = new Float32Array(3);
      positions[0] = (Math.random() - 0.5) * 100;
      positions[1] = Math.random() * 20;
      positions[2] = (Math.random() - 0.5) * 100;
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      
      const mat = new THREE.PointsMaterial({
        color: Math.random() > 0.5 ? 0x00ff00 : 0xff0044,
        size: Math.random() * 0.2 + 0.05,
        transparent: true,
        opacity: Math.random() * 0.5 + 0.5
      });
      
      const points = new THREE.Points(geo, mat);
      this.scene.add(points);
      this.particles.push(points);
    }
  }
  
  spawnDeathEffect(position: THREE.Vector3): void {
    const geo = new THREE.BufferGeometry();
    const positions = new Float32Array(30 * 3);
    
    for (let i = 0; i < 30; i++) {
      positions[i * 3] = position.x + (Math.random() - 0.5) * 2;
      positions[i * 3 + 1] = position.y + Math.random() * 3;
      positions[i * 3 + 2] = position.z + (Math.random() - 0.5) * 2;
    }
    
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    
    const mat = new THREE.PointsMaterial({
      color: 0xff0044,
      size: 0.3,
      transparent: true,
      opacity: 1
    });
    
    const explosion = new THREE.Points(geo, mat);
    this.scene.add(explosion);
    
    let life = 1;
    const animate = () => {
      life -= 0.02;
      mat.opacity = life;
      
      const posAttr = geo.getAttribute('position') as THREE.BufferAttribute;
      for (let i = 0; i < 30; i++) {
        posAttr.array[i * 3 + 1] -= 0.1;
      }
      posAttr.needsUpdate = true;
      
      if (life > 0) {
        requestAnimationFrame(animate);
      } else {
        this.scene.remove(explosion);
        geo.dispose();
        mat.dispose();
      }
    };
    animate();
  }
  
  shakeCamera(): void {
    this.cameraShake.intensity = 0.5;
  }
  
  update(delta: number): void {
    for (const points of this.particles) {
      const positions = points.geometry.getAttribute('position') as THREE.BufferAttribute;
      positions.array[1] += delta * 2;
      
      if (positions.array[1] > 20) {
        positions.array[1] = 0;
        positions.array[0] = (Math.random() - 0.5) * 100;
        positions.array[2] = (Math.random() - 0.5) * 100;
      }
      
      positions.needsUpdate = true;
    }
    
    if (this.cameraShake.intensity > 0.01) {
      this.cameraShake.intensity *= this.cameraShake.decay;
    }
  }
  
  onResize(): void {
    if (this.composer) {
      this.composer.setSize(window.innerWidth, window.innerHeight);
    }
  }
}